import { FRIDGE_FALLBACK_LOGO } from "@/lib/fridge-fallback-image"

/**
 * The single no-image slot filler (FRIDGE-239).
 *
 * Renders the Fridge.com wordmark on a neutral background, and NOTHING else.
 * Every surface that can have an empty image slot uses this: brand-page article
 * cards, brand-kit article cards, and product images.
 *
 * Three properties the card asked for, and how each is met:
 *
 *  1. "Only when there is genuinely no image" — this component has no image
 *     prop and no conditional. It cannot render beside or over a real image,
 *     because the caller only reaches it in the else-branch of an image check.
 *     That is deliberate: a component that took `src` and decided for itself
 *     is exactly how a placeholder ends up layered on top of real content.
 *  2. "Legible at the smallest card in use" — the mark is width-constrained to
 *     a percentage of the slot rather than a fixed pixel size, so it scales
 *     with the card instead of overflowing the small ones or getting lost in
 *     the large ones. The wordmark is ≈5.7:1, so width is the binding
 *     dimension; `max-h-full` stops it overflowing very short letterbox slots.
 *  3. Never announced to assistive tech as content — it is decoration standing
 *     in for absent content, so it carries empty alt + aria-hidden. A screen
 *     reader should hear the card's title, not "Fridge.com logo" on every
 *     imageless card in a grid.
 *
 * `bg-gray-50` matches the neutral letterbox the real-image paths already use,
 * so a grid of mixed cards keeps one background colour.
 */
export function NoImageFallback({ className = "" }: { className?: string }) {
  return (
    <div
      className={`w-full h-full bg-gray-50 flex items-center justify-center ${className}`}
      data-no-image-fallback="true"
    >
      {/* eslint-disable-next-line @next/next/no-img-element */}
      {/* Sized with an inline style, deliberately, NOT Tailwind utilities.
          `w-[55%] max-w-[180px]` was tried first and did not apply: this is a
          new file under Tailwind v4's source detection, and the arbitrary
          utilities never reached the stylesheet, so the browser fell back to
          preflight's `img { max-width: 100% }` and the wordmark rendered
          edge-to-edge at the full 338px slot width. Verified from computed
          style, not assumed. Two inline properties cannot be purged, cannot
          depend on build-order, and cannot silently regress. */}
      <img
        src={FRIDGE_FALLBACK_LOGO}
        alt=""
        aria-hidden="true"
        loading="lazy"
        decoding="async"
        style={{ width: '55%', maxWidth: 180, maxHeight: '100%', objectFit: 'contain', opacity: 0.7 }}
      />
    </div>
  )
}

export default NoImageFallback
